'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowRight, ChevronRight, Bell, Calendar, UserPlus, RotateCcw, Heart, Compass } from 'lucide-react';
import LoginModal from './LoginModal';
import Icon from '@/components/ui/AppIcon';


const latestUpdates = [
  {
    id: 'update-1',
    icon: Calendar,
    en: 'Weekly Training Published',
    zh: '本周训练计划已发布',
    descEN: "This week\'s sessions are live. Check the schedule and join your preferred runs.",
    descZH: '本周训练已上线。查看计划并加入你喜欢的训练。',
    link: '/weekly-plan',
    linkEN: 'View Plan',
    linkZH: '查看计划'
  },
  {
    id: 'update-2',
    icon: Bell,
    en: 'Community Run Coming Soon',
    zh: '社群跑步活动即将举行',
    descEN: 'A community group run is being planned. Stay tuned for the date and location.',
    descZH: '社群集体跑步活动正在筹备中，敬请关注日期和地点。',
    link: '/events',
    linkEN: 'View Events',
    linkZH: '查看活动'
  },
  {
    id: 'update-3',
    icon: UserPlus,
    en: 'Join R3SET Running',
    zh: '加入 R3SET Running',
    descEN: 'Create your account to join sessions, track your progress, and connect with the community.',
    descZH: '创建账户，加入训练、追踪进度，与社群保持联系。',
    link: '/sign-up-login',
    linkEN: 'Create Account',
    linkZH: '创建账户'
  }
];

const whyItems = [
  {
    key: 'consistency',
    icon: RotateCcw,
    en: 'Consistency',
    zh: '一致性',
    descEN: 'Show up every week. Progress compounds over time. No race bibs required.',
    descZH: '每周坚持出现。进步随时间积累。不需要参赛号码布。'
  },
  {
    key: 'recovery',
    icon: Heart,
    en: 'Recovery',
    zh: '恢复',
    descEN: 'Rest days are part of the plan. We train smart, not just hard.',
    descZH: '休息日是计划的一部分。我们聪明地训练，而不仅仅是努力。'
  },
  {
    key: 'exploration',
    icon: Compass,
    en: 'Exploration',
    zh: '探索',
    descEN: 'Trail runs, city loops, waterfront routes. Every run is a new discovery.',
    descZH: '越野跑、城市环线、海滨路线。每次跑步都是新的发现。'
  },
];

export default function HomeClient() {
  const { t } = useLanguage();
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F7F7F2' }}>
      <Header onLoginClick={() => setLoginOpen(true)} />
      {/* ── Editorial Hero ── */}
      <section style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', overflow: 'hidden', minHeight: '92vh' }}>
        {/* Background image */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <img
            src="/assets/images/r3set_hero_editorial.png"
            alt="Early morning city runner silhouette on empty urban street"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Dark gradient overlay */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(7,7,7,0.92) 0%, rgba(7,7,7,0.55) 40%, rgba(7,7,7,0.25) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(7,7,7,0.4) 0%, transparent 60%)' }} />
        </div>

        {/* Hero content — bottom aligned */}
        <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1536, margin: '0 auto', padding: '0 24px 64px', paddingTop: 128 }}>
          <div style={{ maxWidth: 640 }}>
            {/* Label */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 24, backgroundColor: '#B8FF00', padding: '4px 10px', borderRadius: 2 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#070707', display: 'inline-block' }} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#070707', textTransform: 'uppercase', fontFamily: 'var(--font-mono, monospace)' }}>
                {t('Community Open · R3SET Running', '社群开放中 · R3SET Running')}
              </span>
            </div>

            {/* Main headline */}
            <h1 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 'clamp(3rem, 8vw, 6rem)', color: '#FFFFFF', lineHeight: 0.92, marginBottom: 20, letterSpacing: '-0.02em', textTransform: 'uppercase' }}>
              R3SET<br />
              <span style={{ fontWeight: 300, letterSpacing: '0.02em' }}>Running</span>
            </h1>

            {/* Tagline */}
            <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.375rem)', fontWeight: 700, color: 'rgba(255,255,255,0.92)', marginBottom: 12, letterSpacing: '-0.01em', fontFamily: 'var(--font-heading, "Arial Black", sans-serif)' }}>
              {t('Move Better. Run Longer. Stay Consistent.', '动得更好。跑得更远。保持一致。')}
            </p>

            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginBottom: 36, maxWidth: 420, lineHeight: 1.65, fontFamily: 'var(--font-body, sans-serif)' }}>
              {t(
                'A running community built for consistency, recovery, and exploration.',
                '一个专注于一致性、恢复与探索的跑步社群。'
              )}
            </p>

            {/* CTAs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <Link href="/weekly-plan"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderRadius: 2,
                  backgroundColor: '#B8FF00', color: '#070707',
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', textDecoration: 'none',
                  fontFamily: 'var(--font-mono, monospace)',
                  border: '1.5px solid #B8FF00',
                  transition: 'all 0.15s ease',
                }}>
                {t('View Weekly Plan', '查看训练计划')}
                <ArrowRight size={14} />
              </Link>
              <Link href="/sign-up-login"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderRadius: 2,
                  backgroundColor: 'transparent', color: '#FFFFFF',
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', textDecoration: 'none',
                  fontFamily: 'var(--font-mono, monospace)',
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  transition: 'all 0.15s ease',
                }}>
                {t('Join Community', '加入社群')}
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* ── Why R3SET ── */}
      <section style={{ padding: '80px 0', backgroundColor: '#070707', color: '#F7F7F2' }}>
        <div style={{ maxWidth: 1536, margin: '0 auto', padding: '0 24px' }}>
          {/* Section label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 48 }}>
            <div style={{ width: 32, height: 2, backgroundColor: '#B8FF00' }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#B8FF00', fontFamily: 'var(--font-mono, monospace)' }}>
              {t('Why R3SET', '为什么是 R3SET')}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 48 }} className="lg:grid-cols-2">
            {/* Left — copy */}
            <div>
              <h2 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: '#FFFFFF', lineHeight: 1.05, marginBottom: 24, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                {t('Not a race team.', '不是赛跑队。')}<br />
                <span style={{ color: '#B8FF00' }}>{t('A system for resetting.', '一个重置的系统。')}</span>
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, color: 'rgba(247,247,242,0.6)', fontSize: 14, lineHeight: 1.7, fontFamily: 'var(--font-body, sans-serif)' }}>
                <p>{t('R3SET is not a race team.', 'R3SET 不是一支赛跑队。')}</p>
                <p>{t("It's a community built around consistency.", '它是一个围绕一致性建立的社群。')}</p>
                <p style={{ fontWeight: 600, color: 'rgba(247,247,242,0.9)' }}>
                  {t('Run when you can. Recover when you need. Show up again next week.', '能跑就跑。需要恢复就恢复。下周再出现。')}
                </p>
              </div>
              <div style={{ marginTop: 28 }}>
                <Link href="/sign-up-login" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontSize: 12, fontWeight: 700, color: '#B8FF00',
                  textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono, monospace)',
                }}>
                  {t('Become a member', '成为会员')} <ArrowRight size={13} />
                </Link>
              </div>
            </div>

            {/* Right — pillars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {whyItems?.map((item) => {
                const Icon = item?.icon;
                return (
                  <div key={item?.key} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 16,
                    padding: '20px', borderRadius: 2,
                    backgroundColor: 'rgba(247,247,242,0.04)',
                    border: '1px solid rgba(247,247,242,0.1)',
                    transition: 'border-color 0.15s ease',
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 2,
                      backgroundColor: '#B8FF00',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={18} color="#070707" />
                    </div>
                    <div>
                      <h3 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontSize: 14, fontWeight: 900, color: '#FFFFFF', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t(item?.en, item?.zh)}
                      </h3>
                      <p style={{ fontSize: 12, color: 'rgba(247,247,242,0.5)', lineHeight: 1.6, fontFamily: 'var(--font-body, sans-serif)' }}>
                        {t(item?.descEN, item?.descZH)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {/* ── Latest Updates ── */}
      <section style={{ padding: '80px 0', backgroundColor: '#F7F7F2' }}>
        <div style={{ maxWidth: 1536, margin: '0 auto', padding: '0 24px' }}>
          {/* Section header */}
          <div style={{ marginBottom: 40, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#5F625F', fontFamily: 'var(--font-mono, monospace)', display: 'block', marginBottom: 8 }}>
                {t('Community', '社群动态')}
              </span>
              <h2 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#070707', letterSpacing: '-0.02em', textTransform: 'uppercase' }}>
                {t('Latest Updates', '最新动态')}
              </h2>
            </div>
            <div style={{ width: '100%', height: 1, backgroundColor: '#D8D8CE' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 16 }} className="sm:grid-cols-3">
            {latestUpdates?.map((update) => {
              const UpdateIcon = update?.icon;
              return (
                <div
                  key={update?.id}
                  style={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #D8D8CE',
                    borderRadius: 2,
                    padding: 24,
                    display: 'flex', flexDirection: 'column', gap: 12,
                    transition: 'border-color 0.15s ease',
                  }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 2, backgroundColor: '#B8FF00', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UpdateIcon size={16} color="#070707" />
                  </div>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontSize: 13, fontWeight: 900, color: '#070707', marginBottom: 6, lineHeight: 1.3, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                      {t(update?.en, update?.zh)}
                    </h3>
                    <p style={{ fontSize: 12, color: '#5F625F', lineHeight: 1.65, fontFamily: 'var(--font-body, sans-serif)' }}>
                      {t(update?.descEN, update?.descZH)}
                    </p>
                  </div>
                  <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #D8D8CE' }}>
                    <Link
                      href={update?.link}
                      style={{
                        fontSize: 11, fontWeight: 700, color: '#070707',
                        textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        fontFamily: 'var(--font-mono, monospace)',
                        transition: 'color 0.15s ease',
                      }}
                    >
                      {t(update?.linkEN, update?.linkZH)}
                      <ChevronRight size={11} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => setLoginOpen(false)} />
    </div>
  );
}
